if (!customElements.get('wls-product-card-media')) {
  class WlsProductCardMedia extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        img: 'js-product-card-media-img',
      };

      this.bindMethods = {
        onVariantSelect: this._onVariantSelect.bind(this),
      };
    }

    /**
     * Get DOM element references
     * @returns {object}
     */
    get elements() {
      return {
        img: this.querySelector(`.${this.classes.img}`),
      };
    }

    connectedCallback() {
      this.closest('wls-product-card')?.addEventListener('wls:variant-select', this.bindMethods.onVariantSelect);
    }

    disconnectedCallback() {
      this.closest('wls-product-card')?.removeEventListener('wls:variant-select', this.bindMethods.onVariantSelect);
    }

    /**
     * Swap the product image when a variant is selected
     * @param {CustomEvent} event - detail: { imageUrl }
     * @returns {void}
     */
    _onVariantSelect(event) {
      const { imageUrl } = event.detail;
      if (!imageUrl || !this.elements.img) return;
      // Strip srcset/sizes so the browser doesn't prefer them over the new src
      this.elements.img.removeAttribute('srcset');
      this.elements.img.removeAttribute('sizes');
      this.elements.img.setAttribute('src', imageUrl);
    }
  }

  customElements.define('wls-product-card-media', WlsProductCardMedia);
}

if (!customElements.get('wls-product-variant')) {
  class WlsProductVariant extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        btn: 'js-wls-product-variant-btn',
        selected: 'is-selected',
        quickAdd: 'is-quick-add',
      };

      this.bindMethods = {
        onClick: this._onClick.bind(this),
      };

      this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      /** @type {Map<HTMLButtonElement, { enter: Function, leave: Function }>} */
      this._buttonListeners = new Map();
    }

    /**
     * Get all variant buttons
     * @returns {object}
     */
    get elements() {
      return {
        buttons: this.querySelectorAll(`.${this.classes.btn}`),
      };
    }

    connectedCallback() {
      this.addEventListener('click', this.bindMethods.onClick);
      if (!this.isTouch) {
        this._bindButtonHoverListeners();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.bindMethods.onClick);
      this._unbindButtonHoverListeners();
    }

    /**
     * Add mouseenter/mouseleave directly on each button so events fire only
     * on true button entry/exit — not on moves between child spans.
     * @returns {void}
     */
    _bindButtonHoverListeners() {
      this.elements.buttons.forEach((btn) => {
        const enter = () => this._onButtonEnter(btn);
        const leave = () => this._onButtonLeave(btn);
        this._buttonListeners.set(btn, { enter, leave });
        btn.addEventListener('mouseenter', enter);
        btn.addEventListener('mouseleave', leave);
      });
    }

    /**
     * Remove all button hover listeners
     * @returns {void}
     */
    _unbindButtonHoverListeners() {
      this._buttonListeners.forEach(({ enter, leave }, btn) => {
        btn.removeEventListener('mouseenter', enter);
        btn.removeEventListener('mouseleave', leave);
      });
      this._buttonListeners.clear();
    }

    /**
     * Get the closest variant button from a click target
     * Handles clicks on child <span> elements inside the button
     * @param {EventTarget} target
     * @returns {HTMLButtonElement|null}
     */
    _getButton(target) {
      return target.closest(`.${this.classes.btn}`);
    }

    /**
     * Handle click: select variant and update input — form submits via type="submit"
     * @param {MouseEvent} event
     * @returns {void}
     */
    _onClick(event) {
      const btn = this._getButton(event.target);
      if (!btn) return;
      this._selectButton(btn);
      this._dispatchVariantSelect(btn);
    }

    /**
     * On button mouseenter: transfer is-selected to the hovered button and show
     * Quick Add label. All buttons show the quick-add state on hover so a
     * single click adds to cart regardless of whether the button was pre-selected.
     * The last hovered button keeps is-selected when the cursor leaves the panel.
     * @param {HTMLButtonElement} btn
     * @returns {void}
     */
    _onButtonEnter(btn) {
      // Transfer is-selected from whichever button currently holds it
      const currentSelected = this.querySelector(`.${this.classes.btn}.${this.classes.selected}`);
      if (currentSelected && currentSelected !== btn) {
        currentSelected.classList.remove(this.classes.selected);
      }

      btn.classList.add(this.classes.selected, this.classes.quickAdd);
      this._dispatchVariantSelect(btn);
    }

    /**
     * On button mouseleave: only drop the quick-add label. is-selected stays on
     * this button so whichever variant was last active remains selected when
     * the cursor leaves the panel — no revert to the original default.
     * @param {HTMLButtonElement} btn
     * @returns {void}
     */
    _onButtonLeave(btn) {
      btn.classList.remove(this.classes.quickAdd);
    }

    /**
     * Mark a button as selected, reset all others
     * @param {HTMLButtonElement} btn
     * @returns {void}
     */
    _selectButton(btn) {
      this.elements.buttons.forEach((b) => {
        b.classList.remove(this.classes.selected, this.classes.quickAdd);
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add(this.classes.selected);
      btn.setAttribute('aria-pressed', 'true');
    }

    /**
     * Dispatch wls:variant-select with all variant data from button dataset
     * @param {HTMLButtonElement} btn
     * @returns {void}
     */
    _dispatchVariantSelect(btn) {
      this.dispatchEvent(new CustomEvent('wls:variant-select', {
        bubbles: true,
        detail: {
          variantId: btn.dataset.variantId,
          imageUrl: btn.dataset.imageUrl,
          priceId: btn.dataset.priceId,
          available: btn.dataset.available === 'true',
          productUrl: btn.dataset.url,
          sweepsEntries: btn.dataset.sweepsEntries,
        },
      }));
    }
  }

  customElements.define('wls-product-variant', WlsProductVariant);
}

if (!customElements.get('wls-product-card')) {
  class WlsProductCard extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        hidden: 'hidden',
        priceWrapper: 'js-product-card-price-wrapper',
        form: 'js-wls-product-form',
        productUrl: 'js-product-url',
        sharedSweepsEntriesAmount: 'js-shared-sweeps-entries-amount',
      };

      this.bindMethods = {
        onVariantSelect: this._onVariantSelect.bind(this),
      };
    }

    /**
     * Get key DOM elements inside the card
     * @returns {object}
     */
    get elements() {
      return {
        variantIdInput: this.querySelector('input[name="id"]'),
        form: this.querySelector(`.${this.classes.form}`),
        priceWrappers: this.querySelectorAll(`.${this.classes.priceWrapper}`),
      };
    }

    connectedCallback() {
      this.addEventListener('wls:variant-select', this.bindMethods.onVariantSelect);
    }

    disconnectedCallback() {
      this.removeEventListener('wls:variant-select', this.bindMethods.onVariantSelect);
    }

    /**
     * Handle variant selection: update form input + switch visible price wrapper
     * @param {CustomEvent} event - detail: { variantId, imageUrl, priceId, available }
     * @returns {void}
     */
    _onVariantSelect(event) {
      const { variantId, priceId, productUrl, sweepsEntries } = event.detail;
      this._updateVariantInput(variantId);
      this._switchPrice(priceId);
      this._updateProductUrls(productUrl);
      this._updateSweepsEntries(sweepsEntries);
    }

    /**
     * Update the hidden variant ID input value
     * @param {string} variantId
     * @returns {void}
     */
    _updateVariantInput(variantId) {
      const input = this.elements.variantIdInput;
      if (input && variantId) {
        // setAttribute triggers WlsProductForm's MutationObserver so it updates
        // currentFormState.variantId before requestSubmit() is called.
        // Setting .value alone only changes the DOM property, not the attribute.
        input.setAttribute('value', variantId);
      }
    }

    /**
     * Show the price wrapper matching priceId, hide all others
     * @param {string} priceId - matches data-price-id attribute
     * @returns {void}
     */
    _switchPrice(priceId) {
      if (!priceId) return;
      this.elements.priceWrappers.forEach((wrapper) => {
        if (wrapper.dataset.priceId === priceId) {
          wrapper.classList.remove(this.classes.hidden);
        } else {
          wrapper.classList.add(this.classes.hidden);
        }
      });
    }

    /**
     * Update the sweeps entries count displayed on the card
     * @param {string|undefined} sweepsEntries
     * @returns {void}
     */
    _updateSweepsEntries(sweepsEntries) {
      if (sweepsEntries === undefined) return;
      this.querySelectorAll(`.${this.classes.sharedSweepsEntriesAmount}`).forEach((el) => {
        el.textContent = sweepsEntries;
      });
    }

    /**
     * Update all product links on the card to the selected variant's URL
     * @param {string} productUrl - Variant URL (e.g. /products/handle?variant=123)
     * @returns {void}
     */
    _updateProductUrls(productUrl) {
      if (!productUrl) return;
      this.querySelectorAll(`.${this.classes.productUrl}`).forEach((link) => {
        link.setAttribute('href', productUrl);
      });
    }
  }

  customElements.define('wls-product-card', WlsProductCard);
}

if (!customElements.get('wls-product-card-variant-drawer')) {
  class WlsProductCardVariantDrawer extends HTMLElement {
    constructor() {
      super();
      this._loadingButton = null;
      this._trigger = null;
      this._onTriggerClick = this._open.bind(this);
      this._onClick = this._handleClick.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this._onAddSuccess = this._handleAddSuccess.bind(this);
      this._onAddError = this._handleAddError.bind(this);
    }

    get dialog() {
      return this.closest('.js-wls-variant-drawer');
    }

    connectedCallback() {
      const card = this.closest('wls-product-card');
      this._trigger = card?.querySelector('.js-wls-product-variant-more');
      this._trigger?.addEventListener('click', this._onTriggerClick);
      this.dialog?.addEventListener('click', this._onClick);
      card?.addEventListener('wls:add-to-cart-success', this._onAddSuccess);
      card?.addEventListener('wls:add-to-cart-error', this._onAddError);
    }

    disconnectedCallback() {
      this._trigger?.removeEventListener('click', this._onTriggerClick);
      this.dialog?.removeEventListener('click', this._onClick);
      this.closest('wls-product-card')?.removeEventListener('wls:add-to-cart-success', this._onAddSuccess);
      this.closest('wls-product-card')?.removeEventListener('wls:add-to-cart-error', this._onAddError);
      document.removeEventListener('keydown', this._onKeydown);
    }

    _open() {
      this.classList.remove('is-success');
      this._setError('');
      this.dialog?.removeAttribute('hidden');
      document.body.classList.add('wls-lock-scroll');
      document.addEventListener('keydown', this._onKeydown);
      this.querySelector('.js-wls-variant-drawer-close')?.focus();
    }

    _close() {
      this.dialog?.setAttribute('hidden', '');
      document.body.classList.remove('wls-lock-scroll');
      document.removeEventListener('keydown', this._onKeydown);
      this.classList.remove('is-success');
      this._clearLoading();
      this._trigger?.focus();
    }

    _handleClick(event) {
      if (event.target === this.dialog || event.target.closest('.js-wls-variant-drawer-close, .js-wls-variant-drawer-continue')) {
        this._close();
        return;
      }

      if (event.target.closest('.js-wls-variant-drawer-cart')) {
        this._close();
        document.querySelector('cart-drawer')?.open();
        return;
      }

      const addButton = event.target.closest('.js-wls-variant-drawer-add');
      if (!addButton || addButton.disabled) return;
      this._setError('');
      const input = this.closest('wls-product-card')?.querySelector('input[name="id"]');
      if (input) {
        input.value = addButton.dataset.variantId;
        input.setAttribute('value', addButton.dataset.variantId);
      }
      this._loadingButton = addButton;
      addButton.classList.add('is-loading');
    }

    _handleKeydown(event) {
      if (event.key === 'Escape') this._close();
    }

    _handleAddSuccess(event) {
      if (this.dialog?.hasAttribute('hidden')) return;
      this._clearLoading();
      this._setError('');
      if (event.detail.showSuccessScreen) {
        this.classList.add('is-success');
      } else {
        this._close();
      }
    }

    _handleAddError(event) {
      this._clearLoading();
      this._setError(event.detail?.message || 'Unable to add this item to cart.');
    }

    _setError(message) {
      const error = this.querySelector('.js-wls-variant-drawer-error');
      if (error) error.textContent = message;
    }

    _clearLoading() {
      this._loadingButton?.classList.remove('is-loading');
      this._loadingButton = null;
    }
  }

  customElements.define('wls-product-card-variant-drawer', WlsProductCardVariantDrawer);
}

if (!customElements.get('wls-mobile-quick-add')) {
  class WlsMobileQuickAdd extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        closeBtn: 'js-mobile-close',
        addBtn: 'js-mobile-add-btn',
        mobileTrigger: 'js-mobile-trigger',
        dialog: 'js-quick-add-dialog',
        lockScroll: 'wls-lock-scroll',
        successState: 'is-success',
        goToCart: 'js-go-to-cart',
        continueShopping: 'js-continue-shopping',
        successVariant: 'js-success-variant',
        productImg: 'js-mobile-quick-add-image',
      };

      this.bindMethods = {
        onClose: this._onClose.bind(this),
        onAddClick: this._onAddClick.bind(this),
        onDialogClick: this._onDialogClick.bind(this),
        onTriggerClick: this._onTriggerClick.bind(this),
        onAddToCartSuccess: this._onAddToCartSuccess.bind(this),
        onAddToCartError: this._onAddToCartError.bind(this),
        onGoToCart: this._onGoToCart.bind(this),
        onContinueShopping: this._onClose.bind(this),
      };

      /** @type {HTMLButtonElement|null} */
      this._loadingBtn = null;
      /** @type {string|null} */
      this._originalImgSrc = null;
    }

    /**
     * Get DOM element references
     * @returns {object}
     */
    get elements() {
      return {
        dialog: this.closest(`.${this.classes.dialog}`),
        closeBtns: this.querySelectorAll(`.${this.classes.closeBtn}`),
        addButtons: this.querySelectorAll(`.${this.classes.addBtn}`),
        goToCartBtn: this.querySelector(`.${this.classes.goToCart}`),
        continueShoppingBtn: this.querySelector(`.${this.classes.continueShopping}`),
        successVariant: this.querySelector(`.${this.classes.successVariant}`),
      };
    }

    connectedCallback() {
      const {closeBtns , addButtons, dialog, goToCartBtn, continueShoppingBtn } = this.elements;
      this._trigger = this.closest('wls-product-card')?.querySelector(`.${this.classes.mobileTrigger}`);

      closeBtns?.forEach((btn) => btn.addEventListener('click', this.bindMethods.onClose));
      addButtons?.forEach((btn) => btn.addEventListener('click', this.bindMethods.onAddClick));
      dialog?.addEventListener('click', this.bindMethods.onDialogClick);
      this._trigger?.addEventListener('click', this.bindMethods.onTriggerClick);
      this.closest('wls-product-card')?.addEventListener('wls:add-to-cart-success', this.bindMethods.onAddToCartSuccess);
      this.closest('wls-product-card')?.addEventListener('wls:add-to-cart-error', this.bindMethods.onAddToCartError);
      goToCartBtn?.addEventListener('click', this.bindMethods.onGoToCart);
      continueShoppingBtn?.addEventListener('click', this.bindMethods.onContinueShopping);
    }

    disconnectedCallback() {
      const {closeBtns, addButtons, dialog, goToCartBtn, continueShoppingBtn } = this.elements;

      closeBtns?.forEach((btn) => btn.removeEventListener('click', this.bindMethods.onClose));
      addButtons?.forEach((btn) => btn.removeEventListener('click', this.bindMethods.onAddClick));
      dialog?.removeEventListener('click', this.bindMethods.onDialogClick);
      this._trigger?.removeEventListener('click', this.bindMethods.onTriggerClick);
      this.closest('wls-product-card')?.removeEventListener('wls:add-to-cart-success', this.bindMethods.onAddToCartSuccess);
      this.closest('wls-product-card')?.removeEventListener('wls:add-to-cart-error', this.bindMethods.onAddToCartError);
      goToCartBtn?.removeEventListener('click', this.bindMethods.onGoToCart);
      continueShoppingBtn?.removeEventListener('click', this.bindMethods.onContinueShopping);
    }

    /**
     * Open the dialog and lock scroll
     * @returns {void}
     */
    _onTriggerClick() {
      this._setError('');
      this.elements.dialog?.removeAttribute('hidden');
      document.body.classList.add(this.classes.lockScroll);
    }

    /**
     * Close dialog, unlock scroll, and reset success + loading state
     * @returns {void}
     */
    _onClose() {
      this.elements.dialog?.setAttribute('hidden', '');
      document.body.classList.remove(this.classes.lockScroll);
      this.classList.remove(this.classes.successState);
      this._clearLoadingBtn();
      this._setError('');
      if (this._originalImgSrc) {
        const img = this.querySelector(`.${this.classes.productImg}`);
        if (img) {
          img.src = this._originalImgSrc.src;
          if (this._originalImgSrc.srcset) img.setAttribute('srcset', this._originalImgSrc.srcset);
          if (this._originalImgSrc.sizes) img.setAttribute('sizes', this._originalImgSrc.sizes);
        }
        this._originalImgSrc = null;
      }
    }

    _onAddToCartError(event) {
      this._clearLoadingBtn();
      this._setError(event.detail?.message || 'Unable to add this item to cart.');
    }

    _setError(message) {
      const error = this.querySelector('.js-mobile-quick-add-error');
      if (error) error.textContent = message;
    }

    /**
     * Close when clicking outside the inner content area and unlock scroll
     * @param {MouseEvent} event
     * @returns {void}
     */
    _onDialogClick(event) {
      if (event.target === this.elements.dialog) {
        this._onClose();
      }
    }

    /**
     * Update the card's variant input before the form submits.
     * The button is type="submit" so the form submits naturally after this handler.
     * Success screen is shown via wls:add-to-cart-success event from the form.
     * @param {MouseEvent} event
     * @returns {void}
     */
    _onAddClick(event) {
      const variantId = event.currentTarget.dataset.variantId;
      if (!variantId) return;
      this._setError('');
      const input = this.closest('wls-product-card')?.querySelector('input[name="id"]');
      if (input) {
        input.value = variantId;
        input.setAttribute('value', variantId);
      }
      this._loadingBtn = event.currentTarget;
      this._loadingBtn.classList.add('is-loading');
    }

    /**
     * Show the success state with variant info from the cart response
     * @param {CustomEvent} event - detail: { data } (Shopify cart/add response)
     * @returns {void}
     */
    _onAddToCartSuccess(event) {
      if (this.elements.dialog?.hasAttribute('hidden')) return;
      this._clearLoadingBtn();
      this._setError('');
      const { data, showSuccessScreen } = event.detail;

      if (!showSuccessScreen) {
        // Desktop: cart drawer opens normally — just close the dialog
        this._onClose();
        return;
      }

      const variantEl = this.elements.successVariant;
      if (variantEl && data) {
        const size = data.variant_title || '';
        const price = this._formatPrice(data.final_price ?? data.price);
        variantEl.innerHTML = size
          ? `<span>${size}</span><span>${price}</span>`
          : `<span>${price}</span>`;
      }
      
      const img = this.querySelector(`.${this.classes.productImg}`);
      const newImgSrc = data?.featured_image?.url || data?.image;
      if (img && newImgSrc) {
        this._originalImgSrc = { src: img.src, srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes') };
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = newImgSrc;
      }

      this.classList.add(this.classes.successState);
    }

    /**
     * Remove is-loading from the tracked button
     * @returns {void}
     */
    _clearLoadingBtn() {
      if (this._loadingBtn) {
        this._loadingBtn.classList.remove('is-loading');
        this._loadingBtn = null;
      }
    }

    /**
     * Close dialog and open the cart drawer
     * @returns {void}
     */
    _onGoToCart() {
      this._onClose();
      document.querySelector('cart-drawer')?.open();
    }

    /**
     * Format a price in cents using the theme money format
     * @param {number} cents
     * @returns {string}
     */
    _formatPrice(cents) {
      const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
      let formatted = window.WLSNHelpers?.formatMoney(cents, moneyFormat) || `$${(cents / 100).toFixed(2)}`;
      if (formatted.includes('<')) {
        formatted = formatted.replace(/<[^>]*>/g, '').trim();
      }
      return formatted;
    }
  }

  customElements.define('wls-mobile-quick-add', WlsMobileQuickAdd);
}
